import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/Header";


export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Real Pranks" },
      {
        name: "description",
        content:
          "How RossCreations and Real Pranks collect, use, and protect your personal information.",
      },
      { property: "og:title", content: "Privacy Policy — Real Pranks" },
      {
        property: "og:description",
        content: "How we handle your data.",
      },
    ],
  }),
  component: PrivacyPage,
});

const LAST_UPDATED = "April 22, 2026";

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-12 md:py-16">
        <span className="text-[10px] font-bold tracking-[0.25em] text-muted-foreground uppercase">
          Legal
        </span>
        <h1 className="mt-2 text-3xl font-bold md:text-4xl">Privacy policy</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Last updated: {LAST_UPDATED}
        </p>

        <div className="prose prose-invert mt-10 max-w-none space-y-6 text-sm leading-relaxed text-foreground/90">
          <Section title="1. Introduction">
            <p>
              RossCreations (“RossCreations,” “we,” “our,” or “us”) operates the
              Real Pranks website and related services (the “Service”). This
              Privacy Policy explains what information we collect, how we use
              it, and the choices you have. By using the Service, you agree to
              the collection and use of information in accordance with this
              policy.
            </p>
          </Section>

          <Section title="2. Information we collect">
            <p>We collect information in the following ways:</p>
            <ul className="ml-6 list-disc space-y-1">
              <li>
                <strong>Account information</strong> you provide directly:
                name, email, password, phone number, Instagram handle, T-shirt
                size, and gender.
              </li>
              <li>
                <strong>Payment information</strong> processed by our payment
                provider. We do not store full credit card numbers.
              </li>
              <li>
                <strong>Usage data</strong> such as pages visited, videos
                watched, and device and browser information.
              </li>
              <li>
                <strong>Cookies and similar technologies</strong> to keep you
                signed in and to remember your preferences.
              </li>
            </ul>
          </Section>

          <Section title="3. How we use your information">
            <ul className="ml-6 list-disc space-y-1">
              <li>To provide, operate, and maintain the Service.</li>
              <li>
                To process subscriptions and merchandise orders, including
                shipping and customer support.
              </li>
              <li>
                To send transactional emails (receipts, password resets,
                shipping notifications).
              </li>
              <li>
                To send marketing emails about new drops, only if you have
                opted in.
              </li>
              <li>
                To detect, prevent, and respond to fraud, abuse, and security
                incidents.
              </li>
            </ul>
          </Section>

          <Section title="4. Sharing your information">
            <p>
              We do not sell your personal information. We share limited
              information with service providers who help us operate the
              Service (payment processors, email delivery, analytics, customer
              support tooling), and only to the extent needed to perform their
              services. We may also disclose information to comply with the law
              or to protect our rights.
            </p>
          </Section>

          <Section title="5. Data retention">
            <p>
              We retain account information for as long as your account is
              active. You may request deletion at any time by emailing
              members@rosscreations.com. We may retain certain records (e.g.
              tax and payment records) for as long as required by law.
            </p>
          </Section>

          <Section title="6. Your rights">
            <p>
              Depending on where you live, you may have the right to access,
              correct, delete, or export your personal information, and to
              object to certain processing. To exercise any of these rights,
              email members@rosscreations.com.
            </p>
          </Section>

          <Section title="7. Children’s privacy">
            <p>
              The Service is not intended for children under 13. We do not
              knowingly collect personal information from children under 13. If
              you believe a child has provided us with personal information,
              please contact us so we can delete it.
            </p>
          </Section>

          <Section title="8. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. When we do,
              we will revise the “Last updated” date above and, when changes
              are material, notify you by email or in the Service.
            </p>
          </Section>

          <Section title="9. Contact us">
            <p>
              Questions about this policy? Email{" "}
              <a
                href="mailto:members@rosscreations.com"
                className="underline underline-offset-4"
              >
                members@rosscreations.com
              </a>
              .
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-lg font-bold">{title}</h2>
      <div className="mt-2 space-y-2">{children}</div>
    </section>
  );
}
